<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-card color="transparent" flat>
      <v-toolbar color="transparent" flat>
        <v-toolbar-title class="font-weight-bold">{{
          texts.title
        }}</v-toolbar-title>
      </v-toolbar>
      <v-toolbar color="transparent" class="mt-4" flat>
        <v-col cols="4" class="pl-0">
          <label class="input-label"
            >{{ $t("geneticsAndBreeding.selectTrait") }}
            <v-icon>mdi-help-circle-outline</v-icon>
          </label>
          <v-select
            class="trait-select"
            :items="traits"
            item-text="label"
            item-value="value"
            v-model="trait"
            outlined
            dense
            @change="getContemporaryGroupData(true)"
          ></v-select>
        </v-col>
      </v-toolbar>
      <v-card outlined class="v-card__body">
        <h3 class="black--text pb-2">{{ cardTitle }}</h3>
        <v-card-text class="mt-5 pa-0">
          <v-data-table
            :headers="selectedHeaders"
            :items="items"
            :search="search"
            hide-default-footer
            :items-per-page="3"
            :loading="loading"
            :options.sync="tableOptions"
            :server-items-length="totalItems"
            loading-text="$t('loadingPleaseWait')"
          >
            <template v-slot:top>
              <div class="d-flex mt-5">
                <v-col cols="4" class="pl-0">
                  <v-text-field
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    height="5px"
                    placeholder="Search"
                    dense
                    v-model="search"
                    class="shrink"
                  ></v-text-field>
                </v-col>
                <v-spacer></v-spacer>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  @click="download"
                >
                  <v-icon dark> mdi-tray-arrow-down </v-icon>
                </v-btn>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  @click="print"
                >
                  <v-icon dark> mdi-printer-outline </v-icon>
                </v-btn>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="tableOptions.page <= 1"
                  @click="pageChange(false)"
                >
                  <v-icon dark> mdi-chevron-left </v-icon>
                </v-btn>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="tableOptions.page >= totalPages"
                  @click="pageChange(true)"
                >
                  <v-icon dark> mdi-chevron-right </v-icon>
                </v-btn>
                <div class="py-1 title">
                  {{ from }} - {{ to }} of {{ totalItems }}
                </div>
              </div>
            </template>
            <template v-if="items.length > 0" v-slot:body="{ items }">
              <tbody v-if="trait === 'birthWeight'">
                <tr
                  v-for="(item, index) in items"
                  :key="index"
                  style="position: relative; width: 0; height: 0"
                >
                  <td>{{ item.groupCode }}</td>
                  <td>{{ item.groupName }}</td>
                  <td>{{ item.noOfAnimals || 0 }}</td>
                  <td>
                    {{ item.birthYearFrom | formatDateYear }} -
                    {{ item.birthYearTo | formatDateYear }}
                  </td>
                  <td>{{ item.birthSex }}</td>
                  <td>
                    {{ item.birthManagementCode }}
                  </td>
                  <td>
                    {{ item.breederHerdCode }}
                  </td>
                  <td>
                    {{
                      `${getMonthName(
                        item.seasonOfBirthFrom
                      )} to ${getMonthName(item.seasonOfBirthTo)}`
                    }}
                  </td>

                  <td>
                    <a
                      @click.prevent="
                        $router.push({
                          name: 'cgDetail',
                          params: {
                            id: item.id,
                            groupCode: item.groupCode,
                            trait,
                          },
                        })
                      "
                      >{{ $t("View Details") }}</a
                    >
                  </td>
                </tr>
              </tbody>

              <tbody v-else-if="trait === 'weaningWeight'">
                <tr
                  v-for="(item, index) in items"
                  :key="index"
                  style="position: relative; width: 0; height: 0"
                >
                  <td>{{ item.groupCode }}</td>
                  <td>{{ item.groupName }}</td>
                  <td>{{ item.noOfAnimals || 0 }}</td>
                  <td>
                    {{
                      `${item.weaningWeightMin || 0} to ${
                        item.weaningWeightMax || 0
                      }`
                    }}
                  </td>
                  <td>{{ item.dateWeightedFrom | formatDate }}</td>
                  <td>{{ item.weaningSex }}</td>
                  <td>{{ item.primaryBreed }}</td>
                  <td>{{ item.secondaryBreed }}</td>
                  <td>{{ item.conceptionMethod }}</td>
                  <td>
                    <a
                      @click.prevent="
                        $router.push({
                          name: 'cgDetail',
                          params: {
                            id: item.id,
                            groupCode: item.groupCode,
                            trait,
                          },
                        })
                      "
                      >{{ $t("View Details") }}</a
                    >
                  </td>
                </tr>
              </tbody>

              <tbody v-else-if="trait === 'yearlingWeight'">
                <tr
                  v-for="(item, index) in items"
                  :key="index"
                  style="position: relative; width: 0; height: 0"
                >
                  <td>{{ item.groupCode }}</td>

                  <td>{{ item.groupName }}</td>
                  <td>{{ item.noOfAnimals || 0 }}</td>
                  <td>
                    {{
                      `${item.yearlingWeightMin || 0} to ${
                        item.yearlingWeightMax || 0
                      }`
                    }}
                  </td>
                  <td>{{ item.dateWeightedFrom | formatDate }}</td>
                  <td>{{ item.yearlingSex }}</td>
                  <td>
                    <a
                      @click.prevent="
                        $router.push({
                          name: 'cgDetail',
                          params: {
                            id: item.id,
                            groupCode: item.groupCode,
                            trait,
                          },
                        })
                      "
                      >{{ $t("View Details") }}</a
                    >
                  </td>
                </tr>
              </tbody>

              <tbody v-else-if="trait === 'carcassWeight'">
                <tr
                  v-for="(item, index) in items"
                  :key="index"
                  style="position: relative; width: 0; height: 0"
                >
                  <td>
                    {{
                      item.weaningWeightContemporaryGroupId
                        ? item.weaningweightcontemporarygroup?.group_code
                        : item.yearlingweightcontemporarygroup?.group_code ||
                          "N/A"
                    }}
                  </td>
                  <td>{{ item.groupCode }}</td>
                  <td>{{ item.groupName }}</td>
                  <td>{{ item.noOfAnimals || 0 }}</td>
                  <td>
                    {{
                      `${item.daysOnFeed} ${
                        item.daysOnFeed === 1 ? "Day" : "Days"
                      } `
                    }}
                  </td>
                  <td>{{ item.dateOnFeedFrom | formatDate }}</td>
                  <td>{{ item.carcassSex }}</td>
                  <td>{{ item.breedOfDom }}</td>
                  <td>
                    <a
                      @click.prevent="
                        $router.push({
                          name: 'cgDetail',
                          params: {
                            id: item.id,
                            groupCode: item.groupCode,
                            trait,
                          },
                        })
                      "
                      >{{ $t("View Details") }}</a
                    >
                  </td>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-card>
  </div>
</template>

<script>
/* eslint-disable */
import LoadingMixin from "@/mixins/LoadingMixin";
// import axios from "axios";
import {
  getContemporaryGroupByTrait
} from "@/graphql/EPD.gql";
export default {
  name: "ContemporaryGroupReports",
  computed: {
    cardTitle() {
      return `${
        this.traits.find(item => item.value === this.trait).label
      } ${this.$t("geneticsAndBreeding.contemporaryGroups")}`;
    },
    selectedHeaders() {
      if (this.customHeaders.length > 0) {
        return this.customHeaders;
      }
      if (this.trait === "birthWeight") {
        return this.birthWeightHeaders;
      } else if (this.trait === "weaningWeight") {
        return this.weaningWeightHeaders;
      } else if (this.trait === "yearlingWeight") {
        return this.yearlingWeightHeaders;
      } else if (this.trait === "carcassWeight") {
        return this.carcassWeightHeaders;
      }
      return this.birthWeightHeaders;
    },
  },
  data() {
    return {
      breadcrumbs: [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: "/",
        },
        {
          text: this.$t("Genetics & Breeding"),
          disabled: false,
          link: true,
          exact: true,
          to: { name: "Genetics" },
        },
        {
          text: this.$t("Contemporary Group Reports"),
          disabled: true,
        },
      ],
      texts: {
        title: "Contemporary Group Reports",
      },
      traits: [
        {
          label: "Birth Weight",
          value: "birthWeight",
        },
        {
          label: "Weaning Weight",
          value: "weaningWeight",
        },
        {
          label: "Yearling Weight",
          value: "yearlingWeight",
        },
        {
          label: "Carcass Weight",
          value: "carcassWeight",
        },
      ],
      trait: "birthWeight",
      from: 0,
      to: 0,
      tableOptions: {},
      loading: false,
      search: "",
      totalPages: 1,
      totalItems: 0,
      items: [],
      birthWeightHeaders: [
        {
          text: this.$t("geneticsAndBreeding.birthCGCode"),
          align: "start",
          sortable: false,
          value: "groupCode",
        },
        {
          text: this.$t("geneticsAndBreeding.groupName"),
          align: "start",
          sortable: false,
        },
        { text: this.$t("numberOfAnimals"), align: "start", sortable: false },
        {
          text: this.$t("geneticsAndBreeding.birthYear"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.birthSex"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.birthManagementCode"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.breederHerdCode"),
          align: "start",
          sortable: false,
        },
        // { text: this.$t("geneticsAndBreeding.traitsType.birthWeight") },
        // { text: this.$t("geneticsAndBreeding.primaryBreed") },
        {
          text: this.$t("geneticsAndBreeding.seasonofBirth"),
          align: "start",
          sortable: false,
        },
        // { text: this.$t("geneticsAndBreeding.secondaryBreed") },
        // { text: this.$t("geneticsAndBreeding.conceptionMethod") },
        { text: "" },
      ],
      weaningWeightHeaders: [
        {
          text: this.$t("geneticsAndBreeding.weaningCGCode"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.groupName"),
          align: "start",
          sortable: false,
        },
        { text: this.$t("numberOfAnimals"), align: "start", sortable: false },

        {
          text: this.$t("geneticsAndBreeding.traitsType.weaningWeight"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.dateWeight"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.weaningSex"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.primaryBreed"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.secondaryBreed"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.conceptionMethod"),
          align: "start",
          sortable: false,
        },
        { text: "" },
      ],
      yearlingWeightHeaders: [
        {
          text: this.$t("geneticsAndBreeding.yearlingCGCode"),
          align: "start",
          sortable: false,
        },

        {
          text: this.$t("geneticsAndBreeding.groupName"),
          align: "start",
          sortable: false,
        },
        { text: this.$t("numberOfAnimals"), align: "start", sortable: false },

        {
          text: this.$t("geneticsAndBreeding.traitsType.yearlingWeight"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.dateWeight"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.yearlingSex"),
          align: "start",
          sortable: false,
        },
        { text: "" },
      ],
      carcassWeightHeaders: [
        {
          text: this.$t("geneticsAndBreeding.weaningyearlingCGcode"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.carcassCGCode"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.groupName"),
          align: "start",
          sortable: false,
        },
        { text: this.$t("numberOfAnimals"), align: "start", sortable: false },

        {
          text: this.$t("geneticsAndBreeding.daysOnFeed"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.dateOnFeed"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.carcassSex"),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("geneticsAndBreeding.breedOfDom"),
          align: "start",
          sortable: false,
        },

        { text: "" },
      ],
      customHeaders: [],
    };
  },
  methods: {
    async getContemporaryGroups() {
      // const res = await axios.get(`/animalGenetics/epdRequiredFieldsSetting/${this.trait}`)

      // this.startLoading();
      const { data } = await this.$apollo.query({
        query: getContemporaryGroupByTrait,
        fetchPolicy: "no-cache",
        variables: {
          contemporaryGroupListInput: {
                page :  1 ,
                limit :  10 ,
                sortCol :  "createdAt" ,
                sortOrder :  "DESC" ,
                searchPhrase : ""  
            },
          traitType: this.traits.find((item) => item.value === this.trait)
            .value,
        },
      });

      console.log(data, 'data')

    },
    getMonthName(monthNumber) {
      return new Date(`1997-${monthNumber}-1`).toLocaleString("en-US", {
        month: "short",
      });
    },
    snakeCaseToCamelCase(key) {
      return key
        .toLowerCase()
        .split("_")
        .map((part, i) =>
          i > 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part
        )
        .join("");
    },
    async getContemporaryGroupData(fromSelect = false) {
      if (fromSelect) {
        this.tableOptions.page = 1;
      }
      const { data } = await this.$apollo.query({
        query: getContemporaryGroupByTrait,
        fetchPolicy: "no-cache",
        variables: {
          contemporaryGroupListInput: {
                page :  1 ,
                limit :  10 ,
                sortCol :  "createdAt" ,
                sortOrder :  "DESC" ,
                searchPhrase : this.search  
            },
          traitType: this.traits.find((item) => item.value === this.trait)
            .value,
        },
      });
      if (data.getContemporaryGroupByTrait.rows) {
        // debugger
        const rows = data.getContemporaryGroupByTrait.rows;
        this.items = rows.map(item => {
          for (const [key, value] of Object.entries(item)) {
            delete item[key];
            const sanitizedKey = this.snakeCaseToCamelCase(key);
            item[sanitizedKey] = value;
          }
          return item;
        });
        this.totalItems = data.getContemporaryGroupByTrait.count;

        this.totalPages = Math.ceil(
          this.totalItems / this.tableOptions.itemsPerPage
        );
        this.from =
          (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage + 1;
        this.to =
          this.tableOptions.page * this.tableOptions.itemsPerPage -
          (this.tableOptions.itemsPerPage - data.getContemporaryGroupByTrait.rows.length);
      } else {
        this.$notify({
          title: `$t('geneticsAndBreeding.errorFetchingContemporaryGrpMsg)`,
          text: res.data.message,
          type: "error",
        });
      }
    },
    pageChange(t) {
      if (t) {
        this.tableOptions.page = this.tableOptions.page + 1;
        return;
      } else {
        this.tableOptions.page = this.tableOptions.page - 1;
        return;
      }
    },
    resetTableStructure() {
      const currentHeaders = this.selectedHeaders;
      const customHeaders = currentHeaders.filter(item =>
        this.columnsList.includes(item.text)
      );
      this.customHeaders = customHeaders;
    },
    print() {},
    download() {},
  },
  watch: {
    tableOptions: {
      handler() {
        this.getContemporaryGroupData();
      },
      deep: true,
      immediate: false,
    },
    search: {
      handler() {
        setTimeout(() => {
          this.getContemporaryGroupData();
        }, 1000);
       
      },
      deep: true,
    },
  },
  async created() {
    await this.getContemporaryGroups()
  }
};
</script>

<style scoped>
.trait-select >>> .v-input__slot {
  background-color: white;
}

.input-label {
  color: #1e1e1e;
  font-size: 14px;
  margin-bottom: 8px;
  display: block;
}

.input-label i {
  font-size: 20px;
  margin-left: 8px;
}

.v-card__body {
  background-color: #ffffff;
  padding: 24px;
  margin: 16px;
}

table a {
  text-decoration: underline;
}
.v-data-table-header th {
  white-space: nowrap;
}
</style>
